import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import { Typography } from '@material-ui/core';

import { Button } from '@common/components';

import { useTranslation } from '@common/utils';
import { useSettings } from '@common/hooks';
import { Link } from 'react-router-dom';

import * as qs from 'query-string';

import moment from 'moment';

import { ClearIcon } from '@common/components/Icon';

function Summary({ total, totalFees, totalBeds, totalRooms, totalServices, totalMandatory, bookingId, discounts, uninvoicedTotal, isInitial, productsGrouped = [], productById, totalUpgrades, removeReservations }) {
	const classes = useStyles();
	const tr = useTranslation();
	const { locale } = useSelector(state => state.locale);

	const { currencySymbol, isUpgradesEnabled } = useSettings();

	const trackProceedToCheckout = () => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ 'event': 'Proceed to Checkout' });
	};

	const bookableProducts = productsGrouped.filter(p => !p.fee && !p.mandatory && !p.upgrade);

	return (
		<div className={classes.summary}>
			{isUpgradesEnabled && bookableProducts.map((room, index) => (
				<div key={index} className={classes.roomsConatiner}>
					<div className={classes.product}>
						<Link to={`/${room.product}?${qs.stringify({ from: room.from, to: room.to, language: locale })}`} className={classes.link}>
							<div className={classes.image} style={{ backgroundImage: productById[room.product]?.image ? `url(/images/${productById[room.product]?.image })` : 'none' }} />
						</Link>
					</div>
					<div className={classes.info}>
						<div>
							<Typography variant="h4" className={classes.headline}>
								{productById[room.product]?.i18n[locale]?.title || productById[room.product]?.title}
							</Typography>
						</div>
						{room.from && room.to && <div className={classes.dates}>
							<div>
								<b>{tr('Check-in')}</b>
								&nbsp;
								{moment(room.from).format('MMM DD, YYYY')}
							</div>
							<div>
								<b>{tr('Check-out')}</b>
								&nbsp;
								{moment(room.to).format('MMM DD, YYYY')}
							</div>
						</div>
						}
						{removeReservations && !room.order.length &&  (
							<div className={classes.removeBox}>
								<Button className={classes.removeButton} onClick={() => removeReservations(room.reservation)}>
									<ClearIcon className={classes.icon} />
									{tr('remove')}
								</Button>
							</div>
						)}
					</div>
				</div>
			))}
			{!isUpgradesEnabled &&
				<Typography variant="h3">
					{tr('Summary')}
				</Typography>
			}
			<div className={classes.rows}>

				{totalRooms > 0 &&
					<>
						<div className={classes.row}>
							<div>{tr('Rooms')}</div>
							<div>{totalRooms} {currencySymbol}</div>
						</div>
						<div className={classes.separator} />
					</>
				}
				{totalBeds > 0 &&
					<>
						<div className={classes.row}>
							<div>{tr('Beds')}</div>
							<div>{totalBeds} {currencySymbol}</div>
						</div>
						<div className={classes.separator} />
					</>
				}
				{totalFees > 0 &&
					<>
						<div className={classes.row}>
							<div>{tr('City tax')}</div>
							<div>{totalFees} {currencySymbol}</div>
						</div>
						<div className={classes.separator} />
					</>
				}
				{(totalMandatory > 0 || totalUpgrades > 0) &&
					<>
						<div className={classes.row} style={{ paddingBottom: '4px' }}>
							<div>{tr('Extras')}</div>
							<div>{(totalMandatory || 0) + (totalUpgrades || 0)} {currencySymbol}</div>
						</div>
						{productsGrouped.length > 0 && (productsGrouped || []).map((p, i) => {
							return (
								<div key={`product-${i}`}>
									{p.mandatoryBookingProducts?.length > 0 &&
										<>
											{p.mandatoryBookingProducts.map(p =>
												<div key={p._id} className={classes.subRow}>
													<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
													<div>{p.total} {currencySymbol}/{productById[p.product]?.mandatoryType === 'recurring' ? tr('month') : tr('unit')}</div>
												</div>,
											)}
										</>
									}
									{p.upgrade === true && !!productById[p.product] &&
										<div className={classes.subRow}>
											<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
											<div>{p.total} {currencySymbol}/{productById[p.product]?.upgradeType === 'recurring' ? tr('month') : tr('unit')}</div>
										</div>
									}
								</div>
							);
						})}
					</>
				}
				{totalServices > 0 && <div className={classes.row}>
					<div>{tr('Services')}</div>
					<div>{totalServices} {currencySymbol}</div>
				</div>}

				<div className={classes.separator} />

				{discounts.filter(d => d.code && d.appliedAmount >= 0).map((d, i) => <div key={i} className={`${classes.row} ${classes.total}`}>
					<div>
						{tr(`Discount (${d.code})`)}
					</div>
					<div>
						&minus;{d.appliedAmount} {currencySymbol}
					</div>
				</div>)}

				<div className={`${classes.row} ${classes.total}`}>
					<div>{tr(!isInitial && uninvoicedTotal > 0 ? 'Total paid' : 'Total')}</div>
					<div>{total} {currencySymbol}</div>
				</div>

				{!isInitial && uninvoicedTotal > 0 && <div className={`${classes.row} ${classes.total}`}>
					<div>{tr('Total due')}</div>
					<div>{uninvoicedTotal} {currencySymbol}</div>
				</div>}
			</div>

			{(isInitial || uninvoicedTotal > 0) && <Button
				wide={true} color="primary"
				component={Link}
				to={`/checkout/${bookingId}/?language=${locale}`}
				onClick={trackProceedToCheckout}
			>
				{tr('Proceed to checkout')}
			</Button>}
		</div>
	);
}

Summary.propTypes = {
	total: PropTypes.number.isRequired,
	totalFees: PropTypes.number.isRequired,
	totalBeds: PropTypes.number.isRequired,
	totalRooms: PropTypes.number.isRequired,
	totalServices: PropTypes.number.isRequired,
	totalMandatory: PropTypes.number.isRequired,
	bookingId: PropTypes.string.isRequired,
	discounts: PropTypes.array,
	uninvoicedTotal: PropTypes.number,
	isInitial: PropTypes.bool,
	productsGrouped: PropTypes.array,
	productById: PropTypes.object,
	totalUpgrades: PropTypes.number.isRequired,
	removeReservations: PropTypes.func,
};

Summary.defaultProps = {
	discounts: [],
	uninvoicedTotal: 0,
	isInitial: true,
};

export default Summary;

const useStyles = makeStyles(theme => ({
	summary: {
		marginBottom: theme.spacing(5),
		padding: `${theme.spacing(4)}px ${theme.spacing(3)}px`,
		borderRadius: '4px',
		backgroundColor: theme.palette.custom.background,
	},
	rows: {
		padding: `${theme.spacing(2)}px 0`,
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		padding: `${theme.spacing(2)}px 0`,
	},
	subRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		fontSize: 'small',
	},
	total: {
		fontWeight: 600,
	},
	separator: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},
	removeBox: {
		marginTop: theme.spacing(1),
	},
	removeButton: {
		color: theme.palette.primary.light,
		textTransform: 'lowercase',
		padding: 0,
	},
	product: {
		flexDirection: 'row',
		marginBottom: theme.spacing(3),
	},
	link: {
		flex: '0 0 calc(50% + 12px)',
		height: '180px',
	},
	image: {
		flex: 1,
		width: '100%',
		height: '200px',
		backgroundColor: theme.palette.custom.background,
		borderRadius: '4px 0 0',
		backgroundSize: 'cover',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',

		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	roomsConatiner: {
		marginBottom: theme.spacing(4),
	},
}));
